import React, { useEffect, useState } from "react";
import { ChevronDown, Fish } from "lucide-react";
import BrandLogo from "./BrandLogo";

export default function Hero() {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const onScroll = () => setOffset(window.scrollY);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen ocean-gradient overflow-hidden flex items-center">
      {/* colorful rising bubbles */}
      <div className="absolute inset-0 pointer-events-none">
        {[
          { c: "bg-white/40", s: 26, l: 12, d: 0 },
          { c: "bg-orange-300/50", s: 18, l: 24, d: 1.5 },
          { c: "bg-cyan-200/50", s: 34, l: 40, d: 0.6 },
          { c: "bg-yellow-200/50", s: 14, l: 58, d: 2.4 },
          { c: "bg-white/40", s: 22, l: 72, d: 1 },
          { c: "bg-rose-200/50", s: 28, l: 86, d: 3 },
          { c: "bg-cyan-100/50", s: 16, l: 94, d: 0.3 },
        ].map((b, i) => (
          <span
            key={i}
            className={`absolute bottom-24 rounded-full ${b.c} animate-bubble`}
            style={{
              width: `${b.s}px`,
              height: `${b.s}px`,
              left: `${b.l}%`,
              animationDelay: `${b.d}s`,
              animationDuration: `${6 + (i % 3)}s`,
            }}
          />
        ))}
      </div>

      {/* soft blurry blobs */}
      <div className="absolute inset-0 opacity-40">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white/20 blur-2xl animate-blob"
            style={{
              width: `${140 + i * 50}px`,
              height: `${140 + i * 50}px`,
              top: `${(i * 157) % 70}%`,
              left: `${(i * 71) % 85}%`,
              animationDelay: `${i}s`,
            }}
          />
        ))}
      </div>

      {/* Big background logo (wobbling) — hidden on mobile so it never clashes with the text */}
      <div
        className="hidden md:block absolute right-[-4%] top-1/2 -translate-y-1/2 text-white/25 pointer-events-none"
        style={{ transform: `translateY(calc(-50% + ${offset * 0.15}px))` }}
      >
        <div className="animate-bob">
          <BrandLogo variant="white" className="w-[520px] h-[520px] max-w-[70vw] opacity-90" />
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
        <div className="max-w-2xl" style={{ transform: `translateY(${offset * -0.12}px)` }}>
          <span className="inline-flex items-center gap-2 rounded-full bg-white text-teal-700 px-4 py-1.5 text-sm font-bold mb-6 sticker hover-wiggle -rotate-2">
            <Fish className="w-4 h-4" /> London based indie studio
          </span>
          <h1 className="font-display font-800 text-white leading-[0.92] text-5xl sm:text-7xl lg:text-8xl mb-6">
            Sharkbowl
            <br />
            <span className="inline-block animate-float-soft">Games</span>
          </h1>
          <p className="text-white/95 text-lg sm:text-2xl font-medium max-w-xl mb-10">
            A London based games studio crafting bold, joyful video games.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={() => scrollTo("#about")}
              className="group flex items-center gap-3 bg-white text-teal-700 rounded-full pl-2 pr-6 py-2 font-bold sticker hover:-translate-y-1 hover:translate-x-[-2px] transition-transform"
            >
              <span className="w-10 h-10 rounded-full bg-orange-400 text-white flex items-center justify-center group-hover:animate-bounce">
                <ChevronDown className="w-5 h-5" />
              </span>
              Discover our world
            </button>
            <button
              onClick={() => scrollTo("#works")}
              className="text-white font-bold border-2 border-white rounded-full px-6 py-3 hover:bg-white hover:text-teal-700 transition-colors"
            >
              View our games
            </button>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <button
        onClick={() => scrollTo("#about")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/70 hover:text-white animate-bounce"
        aria-label="Scroll down"
      >
        <ChevronDown className="w-7 h-7" />
      </button>

      {/* Wave divider */}
      <div className="absolute bottom-0 left-0 right-0 leading-none text-[#f5fbfc] dark:text-slate-950">
        <svg viewBox="0 0 1440 120" className="w-full h-[80px]" preserveAspectRatio="none">
          <path fill="currentColor" d="M0,64 C240,120 480,20 720,48 C960,76 1200,120 1440,72 L1440,120 L0,120 Z" />
        </svg>
      </div>
    </section>
  );
}